/**
 * Sidebar Navigation Component (Kiosk Portal)
 * Simple flat navigation without groups
 */

import { useMemo } from 'react'
import { useLocation, useTranslation } from '@ufc/core'
import { isRtlLanguage } from '@ufc/portal-shared'
import { cn } from '@ufc/uikit'
import { selectIsAdmin } from '@ufc/domains/admin-context'
import { useAppSelector } from '@/store/hooks'
import type { SidebarNavProps, MenuItem } from '../types'
import {
  MAIN_MENU_ITEMS,
  SECONDARY_MENU_ITEMS,
} from '../constants'
import { NavItem } from './nav-item'

// ============================================
// Helper Functions
// ============================================

function isPathActive(currentPath: string, itemPath: string): boolean {
  return currentPath === itemPath
}

function filterMenuItemsByAdmin(items: MenuItem[], isAdmin: boolean): MenuItem[] {
  return items.filter((item) => !item.isAdminOnly || isAdmin)
}

// ============================================
// Component
// ============================================

export function SidebarNav({ collapsed, onNavigate }: SidebarNavProps) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const isRTL = isRtlLanguage(i18n.language)
  const isAdmin = useAppSelector(selectIsAdmin)

  // Filter menu items based on admin status
  const visibleMainItems = useMemo(
    () => filterMenuItemsByAdmin(MAIN_MENU_ITEMS, isAdmin),
    [isAdmin]
  )

  const visibleSecondaryItems = useMemo(
    () => filterMenuItemsByAdmin(SECONDARY_MENU_ITEMS, isAdmin),
    [isAdmin]
  )

  return (
    <nav className={cn('flex-1 overflow-y-auto transition-[padding,gap] duration-300', collapsed ? 'p-2' : 'p-4')}>
      {!collapsed && (
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 mb-3">
          {t('sidebar.menu')}
        </p>
      )}

      <ul className="space-y-1.5">
        {/* Main Menu Items */}
        {visibleMainItems.map((item) => (
          <li key={item.to}>
            <NavItem
              item={item}
              collapsed={collapsed}
              isActive={isPathActive(location.pathname, item.to)}
              isRTL={isRTL}
              onNavigate={onNavigate}
            />
          </li>
        ))}

        {/* Secondary Menu Items */}
        {visibleSecondaryItems.map((item) => (
          <li key={item.to}>
            <NavItem
              item={item}
              collapsed={collapsed}
              isActive={isPathActive(location.pathname, item.to)}
              isRTL={isRTL}
              onNavigate={onNavigate}
            />
          </li>
        ))}
      </ul>
    </nav>
  )
}
